import { z } from 'zod';

export type PresentationsBrandsCategory = {
  label: string;
  description: string;
  comingSoon?: boolean;
};

export enum PresentationsTabsPath {
  type = '/account/presentations/type',
  selections = '/account/presentations/selections',
  brands = '/account/presentations/brands',
  confirmations = '/account/presentations/confirmation'
}

export enum PresentationsTab {
  Type = 'type',
  Selections = 'selections',
  Brands = 'brands',
  Confirmation = 'confirmation'
}

export const PresentationsDataEntityTypeSchema = z.enum(['category', 'segment', 'businessunit']);
export type PresentationsDataEntityType = z.infer<typeof PresentationsDataEntityTypeSchema>;

export interface SelectOption {
  id: string | number;
  value: string;
  displayName: string;
}

export type ReportRequest = {
  retailerIds: number[];
  entityType?: PresentationsDataEntityType;
  entityId?: string; // Only for non-category entities
  currentStartWeekId: number;
  currentEndWeekId: number;
  comparisonStartWeekId: number;
  comparisonEndWeekId: number;
  categoryIds?: number[];
  subcategoryIds?: string[];
  destinationCurrency: string;
  reportType: string;
  modelName: string;
};

const FormValueSchema = z.object({
  id: z.string(),
  name: z.string()
});

// TypeScript interface for the Presentation object based on API documentation
export interface Presentation {
  vendorId: string;
  presentationId: string;
  description: string;
  displayName: string;
  displayDescription: string;
  slides: any[]; // Array of slide objects
  tabs: any[]; // Array of tab objects
  status: 'ACTIVE' | 'INACTIVE' | 'DRAFT';
  tags: string; // Comma-separated string of tags
  createdAt: string; // ISO date string
  updatedAt: string; // ISO date string
  dataQueries: any[]; // Array of data query objects
  frontendSelections: Record<string, any>; // Key-value pairs for frontend selections
  frontendValues: Record<string, any>; // Key-value pairs for frontend values
  userInputs: Record<string, any>; // Key-value pairs for user inputs
}

export interface PresentationsResponse {
  presentations: Presentation[];
  total: number;
  page?: number;
  pageSize?: number;
}

// Step 1: Report Type Schema
export const ReportTypeStepSchema = z.object({
  reportType: FormValueSchema.refine((data) => data && data.id && data.name, { message: 'Report Type is required' })
});

export const SelectionsStepSchema = z.object({
  currency: z.string().nonempty('Currency is required'),
  retailerIds: z.array(FormValueSchema),
  entityType: FormValueSchema,
  entityId: FormValueSchema.optional(),
  categoryIds: z.array(FormValueSchema).optional(),
  subcategoryIds: z.array(FormValueSchema).optional()
})
  .refine(
    (data) => data.retailerIds.length > 0,
    {
      message: 'Please select at least one retailer',
      path: ['retailerIds']
    }
  )
  .superRefine((data, ctx) => {
    if (data.entityType.id === PresentationsDataEntityTypeSchema.Enum.category) {
      const hasCategories = data.categoryIds && data.categoryIds.length > 0;
      const hasSubcategories = data.subcategoryIds && data.subcategoryIds.length > 0;

      if (!hasCategories && !hasSubcategories) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Either Categories or Subcategories must be selected',
        });
      }
    }
  })
  .refine(
    (data) => data.entityType.id === PresentationsDataEntityTypeSchema.Enum.category || data.entityId.id,
    (data) => {
      const isSegment = data.entityType.id === PresentationsDataEntityTypeSchema.Enum.segment;
      const prefix = isSegment ? 'Segment' : 'Business Unit';

      return {
        message: prefix + ' cannot be empty',
        path: ['entityId']
      };
    }
  );

// Step 3: Brands Schema
export const BrandsStepSchema = z.object({
  brandGroupings: FormValueSchema
});

export const PresentationFormSchema = z.intersection(ReportTypeStepSchema, SelectionsStepSchema, BrandsStepSchema);

export type ReportTypeStep = z.infer<typeof ReportTypeStepSchema>;
export type SelectionsStep = z.infer<typeof SelectionsStepSchema>;
export type BrandsStep = z.infer<typeof BrandsStepSchema>;

export type PresentationForm = z.infer<typeof PresentationFormSchema>;

export type FormValue = z.infer<typeof FormValueSchema>;
